import { object, pipe } from "valibot";
import { errorTranslation, normalizedLiteral, requireText } from "../../../utils/validation/base";
import { MAX_CHARACTERS_MD } from "../../../constants";
import InputModal from "../../../components/modal/InputModal";
import { useTranslation } from "../../../state/i18n";
import { TextInput } from "../../../components/inputs/TextInput";
import { Translation } from "../../../utils/Translation";
import { type Component, untrack } from "solid-js";
import ApiService from "../../../services/ApiService";
import type SubmissionId from "../../../state/SubmissionId";
import GenericFailureModal from "../../GenericFailureModal";
import type FormId from "../../../state/FormId";
import { useModalManager } from "../../../components/modal/ModalManager";
import { keyFromSelector } from "i18next";

type DeleteSubmissionsModalProps = {
  formName: string;
  submissionIds: SubmissionId[];
  formId: FormId;
  onSuccess: (submissionId: SubmissionId[]) => void;
};

const deleteFormSchema = (formName: string) =>
  object({
    name: pipe(
      requireText(MAX_CHARACTERS_MD),
      normalizedLiteral(
        formName,
        errorTranslation(keyFromSelector(($) => $.DELETE_FORM_MODAL_INPUT_NAME_VALIDATION_NOT_EQUAL)),
      ),
    ),
  });

const DeleteSubmissionsModal: Component<DeleteSubmissionsModalProps> = (props) => {
  const { t } = useTranslation();
  const { replace } = useModalManager();

  return (
    <InputModal
      variant="destructive"
      title={t(($) => $.DELETE_SUBMISSIONS_MODAL_TITLE, { count: props.submissionIds.length })}
      confirmLabel={t(($) => $.DELETE_SUBMISSIONS_MODAL_ACTIONS_DELETE)}
      cancelLabel={t(($) => $.DELETE_SUBMISSIONS_MODAL_ACTIONS_CANCEL)}
      onConfirm={() =>
        new Promise((resolve, reject) => {
          ApiService.deleteSubmissions().mutate(
            untrack(() => ({
              submissionIds: props.submissionIds,
              formId: props.formId,
            })),
            {
              onSuccess: () => {
                untrack(() => props.onSuccess(props.submissionIds));
                resolve();
              },
              onError: (err) => {
                replace({
                  content: () => (
                    <GenericFailureModal
                      title={t(($) => $.DELETE_SUBMISSIONS_FAILURE_MODAL_TITLE, { count: props.submissionIds.length })}
                      error={err}
                    />
                  ),
                });
                reject(err);
              },
            },
          );
        })
      }
      schema={deleteFormSchema(props.formName)}
    >
      {({ value, updateValue, error }) => (
        <div class="flex flex-col gap-6">
          <div class="text-base-regular wrap-break-word text-secondary">
            <Translation
              key={keyFromSelector(($) => $.DELETE_SUBMISSIONS_DESCRIPTION)}
              options={{ formName: props.formName, count: props.submissionIds.length }}
              components={{
                b: (props) => <span class="text-base-semibold text-primary">{props.children}</span>,
              }}
            />
          </div>

          <TextInput
            label={t(($) => $.DELETE_SUBMISSIONS_MODAL_INPUT_NAME)}
            value={value("name")}
            required
            onBlur={(e) => {
              updateValue("name", e.target.value);
            }}
            onInput={(e) => {
              updateValue("name", e.target.value);
            }}
            errorMessage={error("name")}
          />
        </div>
      )}
    </InputModal>
  );
};

export default DeleteSubmissionsModal;
